import React, { createContext, useState, useContext, useCallback, useMemo, useEffect } from 'react';
import type { ReactNode } from 'react';

import { QueryIntent } from '@atlassian/search-ai';
import { isQuestion } from '@atlassian/search-ai/question-detection';

import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useAISearchSupplier } from '@confluence/navigation-search-dialog/entry-points/useAISearchSupplier.ts';

import { useFilterSupplier } from './useFilterSupplier.ts';
import { generateUpdatedQueryParams, initializeFilters, areFiltersSelected } from './utils/filters.tsx';
import {
	FilterIds,
	LastModifiedValues,
	OptionIds,
	defaultFilterState,
	defaultOptionValue,
} from './AdvancedSearch-constants.ts';
import type {
	FilterOption,
	FilterStateType,
	QueryType,
	SearchInputContextType,
	ToggleOptionArgs,
} from './AdvancedSearch-types.ts';
import { useQueryIntent } from './utils/queryPreprocessing/use-query-intent.ts';

export const SearchInputContext = createContext<SearchInputContextType>({
	searchQuery: '',
	filters: defaultFilterState,
	cql: undefined,
	queryType: 'AGGQuery',
	isHydrated: false,
	toggleOption: () => {},
	updateCustomDateRange: () => {},
	resetOptions: () => {},
	toggleDateOption: () => {},
	resetDateOptions: () => {},
	onSearchQueryChange: () => {},
	clearFilters: () => {},
	sessionHasAppliedFilters: false,
	sessionHasUserInput: false,
	onClickAskAI: () => {},
	showSearchAI: false,
	aiTriggeredByAskAI: false,
	aiSearchConfig: undefined,
});

export const useSearchInputContext = () => useContext(SearchInputContext);

export const SearchInputProvider = ({ children }: { children: ReactNode }) => {
	const { getQueryParams, setQueryParams } = useContext(RoutesContext);
	const queryParams = getQueryParams();
	const [filters, setFilters] = useState<FilterStateType>(initializeFilters(queryParams));

	const setUpdatedQueryParams = useCallback(
		(updatedFilters: FilterStateType) => {
			const updatedQueryParams = generateUpdatedQueryParams(updatedFilters);
			setQueryParams({
				...updatedQueryParams,
			});
		},
		[setQueryParams],
	);

	const { loading } = useFilterSupplier({
		queryParams,
		filters,
		setFilters,
		setUpdatedQueryParams,
	});
	const cqlQuery = queryParams?.cql;
	const searchQuery = queryParams?.text?.toString() || '';

	const { locale } = useSessionData();
	const { loading: queryIntentLoading, queryIntent } = useQueryIntent(searchQuery);

	const shouldShowAIAnswer = useCallback(() => {
		const isAiAnswerEnabled = queryParams?.aiAnswer === 'true' && searchQuery.length > 0;
		const queryIsQuestion = isQuestion({
			locale,
			query: searchQuery,
		});
		const isPersonIntent =
			!queryIntentLoading &&
			queryIntent === QueryIntent.PERSON &&
			queryParams?.aiAnswer !== 'false';

		return isAiAnswerEnabled || queryIsQuestion || isPersonIntent;
	}, [locale, queryParams?.aiAnswer, searchQuery, queryIntentLoading, queryIntent]);

	const [showSearchAI, setShowSearchAI] = useState(shouldShowAIAnswer);
	const [aiTriggeredByAskAI, setAITriggeredByAskAI] = useState(false);

	useEffect(() => {
		const aiAnswerEnabled = shouldShowAIAnswer();
		setShowSearchAI(aiAnswerEnabled);
	}, [shouldShowAIAnswer]);

	const { aiSearchConfig } = useAISearchSupplier({});

	const toggleOption = useCallback(
		({ filterId, toggledOption, isMulti }: ToggleOptionArgs) => {
			const updatedFilters = { ...filters };
			const existingOption = filters[filterId].find(
				(filterOption) => filterOption?.value === toggledOption?.value,
			);
			if (existingOption) {
				updatedFilters[filterId] = updatedFilters[filterId].map((option) => {
					if (option.value === toggledOption.value) {
						return { ...option, isSelected: !option.isSelected };
					}
					return isMulti ? option : { ...option, isSelected: false };
				});
			} else {
				const newSelectedOption = {
					...toggledOption,
					isSelected: true,
				};
				updatedFilters[filterId] = updatedFilters[filterId].map((option) => {
					return isMulti ? option : { ...option, isSelected: false };
				});
				updatedFilters[filterId] = [...updatedFilters[filterId], newSelectedOption];
			}

			setFilters(updatedFilters);
			setUpdatedQueryParams(updatedFilters);
		},
		[filters, setUpdatedQueryParams],
	);

	const resetDateOptions = useCallback(() => {
		const updatedFilters = { ...filters };
		updatedFilters[FilterIds.DATE] = defaultFilterState[FilterIds.DATE];
		updatedFilters[FilterIds.DATE][OptionIds.LAST_MODIFIED] = defaultFilterState[FilterIds.DATE][
			OptionIds.LAST_MODIFIED
		].map((option) => ({
			...option,
			isSelected: false,
		}));
		updatedFilters[FilterIds.DATE][OptionIds.FROM] =
			defaultFilterState[FilterIds.DATE][OptionIds.FROM];
		updatedFilters[FilterIds.DATE][OptionIds.TO] = defaultFilterState[FilterIds.DATE][OptionIds.TO];
		setFilters(updatedFilters);
		setUpdatedQueryParams(updatedFilters);
	}, [filters, setUpdatedQueryParams]);

	const toggleDateOption = useCallback(
		(filterId: string, option: FilterOption) => {
			const { optionId, value } = option;
			const updatedFilters = { ...filters };
			if (optionId === OptionIds.LAST_MODIFIED) {
				updatedFilters[filterId] = {
					...updatedFilters[filterId],
					[OptionIds.LAST_MODIFIED]: updatedFilters[filterId][OptionIds.LAST_MODIFIED].map(
						(dateOption) =>
							dateOption.value === value && dateOption.value !== defaultOptionValue
								? { ...dateOption, isSelected: !option.isSelected }
								: { ...dateOption, isSelected: false },
					),
					[OptionIds.TO]: {
						...updatedFilters[filterId][OptionIds.TO],
						isSelected:
							value === LastModifiedValues.CUSTOM
								? updatedFilters[filterId][OptionIds.TO].isSelected
								: false,
					},
					[OptionIds.FROM]: {
						...updatedFilters[filterId][OptionIds.FROM],
						isSelected:
							value === LastModifiedValues.CUSTOM
								? updatedFilters[filterId][OptionIds.FROM].isSelected
								: false,
					},
				};
			} else {
				updatedFilters[filterId] = {
					...updatedFilters[filterId],
					[`${optionId}`]: { ...option, isSelected: !option.isSelected },
				};
			}
			setFilters(updatedFilters);
			setUpdatedQueryParams(updatedFilters);
		},
		[filters, setUpdatedQueryParams],
	);

	const updateCustomDateRange = useCallback(
		(selectedFromOption: FilterOption, selectedToOption: FilterOption) => {
			const updatedFilters = { ...filters };
			updatedFilters[FilterIds.DATE] = {
				...updatedFilters[FilterIds.DATE],
				[OptionIds.FROM]: {
					...selectedFromOption,
					isSelected: selectedFromOption.isSelected,
				},
				[OptionIds.TO]: {
					...selectedToOption,
					isSelected: selectedToOption.isSelected,
				},
			};
			setFilters(updatedFilters);
			setUpdatedQueryParams(updatedFilters);
		},
		[filters, setUpdatedQueryParams],
	);

	const resetOptions = useCallback(
		(filterId: string) => {
			const updatedFilters = { ...filters };
			updatedFilters[filterId] = updatedFilters[filterId].map((option) => {
				return { ...option, isSelected: false };
			});
			setFilters(updatedFilters);
			setUpdatedQueryParams(updatedFilters);
		},
		[filters, setUpdatedQueryParams],
	);

	const onSearchQueryChange = useCallback(
		(newQuery: string) => {
			const queryIsQuestion = isQuestion({
				locale,
				query: newQuery,
			});
			if (queryIsQuestion) {
				setShowSearchAI(true);
				setAITriggeredByAskAI(false);
			} else {
				setShowSearchAI(false);
			}
			setQueryParams({ text: newQuery || undefined });
		},
		[setQueryParams, locale],
	);

	const onClickAskAI = useCallback(
		(newQuery: string) => {
			setShowSearchAI(true);
			setAITriggeredByAskAI(true);
			setQueryParams({ text: newQuery });
		},
		[setQueryParams],
	);

	const clearFilters = useCallback(() => {
		const updatedFilters = { ...filters };
		Object.keys(defaultFilterState).forEach((filterId) => {
			if (filterId === FilterIds.DATE) {
				updatedFilters[FilterIds.DATE] = defaultFilterState[FilterIds.DATE];
				updatedFilters[FilterIds.DATE][OptionIds.LAST_MODIFIED] = defaultFilterState[
					FilterIds.DATE
				][OptionIds.LAST_MODIFIED].map((option) => ({
					...option,
					isSelected: false,
				}));
				updatedFilters[FilterIds.DATE][OptionIds.FROM] =
					defaultFilterState[FilterIds.DATE][OptionIds.FROM];
				updatedFilters[FilterIds.DATE][OptionIds.TO] =
					defaultFilterState[FilterIds.DATE][OptionIds.TO];
			} else {
				updatedFilters[filterId] = filters[filterId].map((option) => ({
					...option,
					isSelected: false,
				}));
			}
		});
		setFilters(updatedFilters);
		setUpdatedQueryParams(updatedFilters);
	}, [filters, setUpdatedQueryParams]);

	const hasAppliedFilters = areFiltersSelected(filters, false);
	const queryType: QueryType =
		cqlQuery && !hasAppliedFilters && !searchQuery ? 'CQLQuery' : 'AGGQuery';

	const contextValue = useMemo(
		() => ({
			searchQuery,
			filters,
			cql: cqlQuery,
			queryType,
			isHydrated: !loading,
			onSearchQueryChange,
			toggleOption,
			resetOptions,
			toggleDateOption,
			updateCustomDateRange,
			resetDateOptions,
			clearFilters,
			sessionHasAppliedFilters: hasAppliedFilters,
			sessionHasUserInput: !!searchQuery || areFiltersSelected(filters, true),
			onClickAskAI,
			showSearchAI,
			aiTriggeredByAskAI,
			aiSearchConfig,
		}),
		[
			clearFilters,
			cqlQuery,
			filters,
			loading,
			onSearchQueryChange,
			resetOptions,
			searchQuery,
			toggleDateOption,
			updateCustomDateRange,
			resetDateOptions,
			toggleOption,
			onClickAskAI,
			showSearchAI,
			aiTriggeredByAskAI,
			aiSearchConfig,
			hasAppliedFilters,
			queryType,
		],
	);

	return <SearchInputContext.Provider value={contextValue}>{children}</SearchInputContext.Provider>;
};
